import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import { GET_MEDIA_QUERY_KEY } from '@/state/queries/media/use-get-media.ts';
import type { MediaDto } from '@/types/dtos/media/media.dto.ts';
import type { UpdateMediaDto } from '@/types/dtos/media/request/update-media.dto.ts';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions.ts';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions.ts';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows.ts';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow.ts';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session.ts';
import { GET_CONTRIBUTOR_SUMMARY_QUERY_KEY } from '@/state/queries/contributors/user-contributors.ts';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto.ts';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import { patchMediaIsRequestedInQueryCache } from '@/state/mutations/media/patch-media-is-requested-cache';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useEventStore } from '@/state/stores/use-event-store';
import { invalidateSimulationCueSheet } from '@/state/queries/player/use-get-simulation-cue-sheet';

type UpdateMediaOptimisticContext = {
  previousQueryEntries: Array<[QueryKey, unknown]>;
  previousFlowStore: {
    flow?: FlowDto;
    flowPlan?: FlowDto;
    flowOrganize?: FlowDto;
  };
  previousEventStore: {
    eventSessions?: SessionDto[];
    eventSessionsPlan?: SessionDto[];
    eventSessionsOrganize?: SessionDto[];
  };
};

function snapshotCaches(queryClient: ReturnType<typeof useQueryClient>): UpdateMediaOptimisticContext {
  const flowState = useFlowStore.getState();
  const eventState = useEventStore.getState();
  return {
    previousQueryEntries: [
      ...queryClient.getQueriesData({ queryKey: [GET_FLOW_QUERY_KEY] }),
      ...queryClient.getQueriesData({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] }),
      ...queryClient.getQueriesData({ queryKey: [GET_SESSION_QUERY_KEY] }),
      ...queryClient.getQueriesData({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] }),
      ...queryClient.getQueriesData({ queryKey: [GET_MEDIA_QUERY_KEY] }),
    ],
    previousFlowStore: {
      flow: flowState.flow,
      flowPlan: flowState.flowPlan,
      flowOrganize: flowState.flowOrganize,
    },
    previousEventStore: {
      eventSessions: eventState.eventSessions,
      eventSessionsPlan: eventState.eventSessionsPlan,
      eventSessionsOrganize: eventState.eventSessionsOrganize,
    },
  };
}

function restoreCaches(queryClient: ReturnType<typeof useQueryClient>, context: UpdateMediaOptimisticContext) {
  for (const [queryKey, data] of context.previousQueryEntries) {
    queryClient.setQueryData(queryKey, data);
  }
  useFlowStore.setState(context.previousFlowStore);
  useEventStore.setState(context.previousEventStore);
}

function invalidateAfterUpdate(queryClient: ReturnType<typeof useQueryClient>) {
  // Mark stale only — do not await refetchQueries. GET_FLOW can be starved ~10–15s by cue rebuild
  // after flowInvalidated, which previously kept the confirm modal spinning.
  void queryClient.invalidateQueries({ queryKey: [GET_MEDIA_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [GET_SESSION_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [GET_CONTRIBUTOR_SUMMARY_QUERY_KEY] });
  void invalidateSimulationCueSheet(queryClient);
}

export const useUpdateMediaMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updateMediaDto: UpdateMediaDto) => {
      const token = await getToken();
      return updateMedia(updateMediaDto, token ?? '');
    },
    onMutate: async (variables) => {
      if (variables.isRequested === undefined) {
        return undefined;
      }

      await Promise.all([
        queryClient.cancelQueries({ queryKey: [GET_MEDIA_QUERY_KEY] }),
        queryClient.cancelQueries({ queryKey: [GET_FLOW_QUERY_KEY] }),
        queryClient.cancelQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] }),
        queryClient.cancelQueries({ queryKey: [GET_SESSION_QUERY_KEY] }),
        queryClient.cancelQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] }),
      ]);

      const context = snapshotCaches(queryClient);
      patchMediaIsRequestedInQueryCache(queryClient, {
        mediaId: variables.id,
        isRequested: variables.isRequested,
      });
      return context;
    },
    onError: (_error, variables, context) => {
      if (variables.isRequested !== undefined && context) {
        restoreCaches(queryClient, context);
      }
    },
    onSuccess: (_data, variables) => {
      if (variables.isRequested !== undefined) {
        // Re-apply in case a concurrent refetch raced past onMutate.
        patchMediaIsRequestedInQueryCache(queryClient, {
          mediaId: variables.id,
          isRequested: variables.isRequested,
        });
      }
      invalidateAfterUpdate(queryClient);
    },
  });
};

const updateMedia = async (updateMediaDto: UpdateMediaDto, token: string): Promise<MediaDto> => {
  const url = `${import.meta.env.VITE_API_URL}/media/${updateMediaDto.id}`;
  return sendJSONRequest({
    url,
    method: METHOD.PATCH,
    body: updateMediaDto,
    token,
  });
};
